import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db, auth } from "@/lib/firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import EventCard, { EventDoc } from "@/events/EventCard";
import { addFavorite, removeFavorite } from "@/events/favorites";

export default function EventDetailPage() {
  const { eid } = useParams();
  const [ev, setEv] = useState<any | null>(null);
  const [organizerProfile, setOrganizerProfile] = useState<{
    displayName?: string | null;
    avatarUrl?: string | null;
    username?: string | null;
    isLgs?: boolean;
  } | null>(null);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  useEffect(() => {
    if (!eid) return;
    getDoc(doc(db, "events", eid)).then((s) =>
      setEv(s.exists() ? { id: s.id, ...s.data() } : null),
    );
  }, [eid]);

  useEffect(() => {
    const uid = ev?.organizerUid;
    if (!uid) return;
    getDoc(doc(db, "users", uid)).then((snap) => {
      if (!snap.exists()) return;
      const d = snap.data() || {};
      setOrganizerProfile({
        displayName: d.storeName || d.displayName || d.name || d.username || null,
        username: d.username || null,
        avatarUrl: d.avatarUrl || d.storeImage || d.photoURL || d?.lgs?.avatar_url || null,
        isLgs: !!(d.isLgs || d.roles?.lgs || d.accountType === "LGS"),
      });
    });
  }, [ev?.organizerUid]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !eid) return;
    const favDoc = doc(db, "users", user.uid, "events_favorited", eid);
    const unsub = onSnapshot(favDoc, (s) => setIsFavorite(s.exists()));
    return () => unsub();
  }, [eid]);

  if (!ev) return <div className="p-4 text-indigo-200">Loading event…</div>;

  const toDate = (x: any) => (x?.toDate ? x.toDate() : new Date(x));
  const derivedIsLgs = !!(ev.organizerIsLGS || ev.locationType === "LGS" || organizerProfile?.isLgs);
  const derivedUsername = ev.organizerUsername || organizerProfile?.username || "";
  const derivedAvatar =
    ev.organizerAvatarUrl ||
    (derivedIsLgs ? organizerProfile?.avatarUrl || null : null) ||
    null;

  const card: EventDoc = {
    id: ev.id,
    title: ev.title,
    details: ev.details,
    formatTypes: ev.formatTypes || [],
    visibility: ev.visibility || "public",
    startTime: toDate(ev.startTime),
    endTime: ev.endTime ? toDate(ev.endTime) : null,
    organizerUid: ev.organizerUid,
    organizerUsername: derivedUsername,
    organizerDisplayName: ev.organizerDisplayName || organizerProfile?.displayName || derivedUsername,
    organizerAvatarUrl: derivedAvatar,
    organizerIsLGS: derivedIsLgs,
    images: Array.isArray(ev.images) ? ev.images : undefined,
    distanceMiles: null,
    favoritesCount: ev.favoritesCount ?? 0,
    eventType: ev.eventType || null,
    address: ev.address || ev.venue?.address || null,
    locationType: ev.locationType || (derivedIsLgs ? "LGS" : null),
    location:
      ev.location ||
      (ev.venue
        ? { lat: Number(ev.venue.lat), lng: Number(ev.venue.lng), name: ev.venue.name ?? null }
        : null),
  };

  return (
    <div className="container mx-auto p-3 max-w-3xl">
      <EventCard
        ev={card}
        isFavorite={isFavorite}
        onFavoriteClick={async () => {
          const user = auth.currentUser;
          if (!user) { alert("Sign in to favorite events."); return; }
          try {
            if (isFavorite) {
              await removeFavorite(card.id);
            } else {
              await addFavorite(card.id);
            }
          } catch (e) {
            console.warn("Favorite toggle failed", e);
          }
        }}
        onShareClick={() => {
          const href = `/${encodeURIComponent(card.organizerUsername || "")}?tab=events&focus=${encodeURIComponent(card.id)}`;
          navigator.clipboard?.writeText(`${location.origin}${href}`).catch(()=>{});
          window.open(href, "_blank");
        }}
      />
    </div>
  );
}

